import { Stack, styled, Tooltip, Typography } from '@mui/material';
import { ConditionOperatorEnum, type Condition } from '@repo/api-gw-sdk';
import classNames from 'classnames';
import React from 'react';

import { useSourceAccounts } from '@/contexts/useSourceAccounts';
import {
  ApplicationsLogosWithAliases,
  CloudProviders,
  Environments,
  SensitivityLevels,
} from '@/data/inventory/data';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import { type FilterProperty } from '@/types/advanceFilter';

import { OPERATORS } from './OperatorSelect';

import { CircleImage } from '../shared/circleImage';

const ROW_GAP = 12;

export const ConditionContainer = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'hideBorder' && prop !== 'enableHoverIndications',
})(
  ({
    hideBorder,
    enableHoverIndications,
  }: {
    hideBorder?: boolean;
    enableHoverIndications?: boolean;
  }) => ({
    width: 'fit-content',
    gap: '4px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'start',
    border: hideBorder ? undefined : '1px solid var(--mui-palette-divider)',
    borderRadius: '4px',
    padding: '12px 16px',
    fontWeight: 600,
    position: 'relative',
    [`&.${ConditionOperatorEnum.And},&.${ConditionOperatorEnum.Or}`]: {
      alignItems: 'flex-start',
      gap: ROW_GAP,
    },
    '&.hovered': {
      borderColor: 'var(--mui-palette-primary-main)',
    },
    ...(enableHoverIndications
      ? {
          '&:hover': {
            borderColor: 'var(--mui-palette-primary-main)',
          },
        }
      : {}),
    '&.root': {
      border: 'none',
      padding: 0,
      flexDirection: 'column',
    },
    '&.highlighted-above::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      left: 0,
      right: 0,
      top: -7,
      borderTop: '1px solid var(--mui-palette-primary-main)',
      height: 1,
    },
    '&.highlighted-below::after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: -7,
      borderTop: '1px solid var(--mui-palette-primary-main)',
      height: 1,
    },
    '&.compact': {
      padding: 0,
      border: 0,
      '> img': { height: '100%' },
    },
  })
);

export const ConditionGroup = styled(ConditionContainer)({
  paddingLeft: '50px',
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: ROW_GAP,
  '&.compact': {
    gap: 6,
  },
});

const ConditionOperator = styled('div')({
  position: 'absolute',
  left: '-40px',
  top: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: '4px',
  transform: `translateY(calc(-50% + ${ROW_GAP / 2}px))`,
  fontWeight: 400,
  fontSize: 12,
  '&::before': {
    content: '""',
    display: 'block',
    width: '12px',
    height: '12px',
    borderRadius: '4px 0 0 0',
    borderColor: 'currentColor',
    borderWidth: '1px 0 0 1px',
  },
  '&::after': {
    content: '""',
    display: 'block',
    width: '12px',
    height: '12px',
    borderRadius: '0 0 0 4px',
    borderColor: 'currentColor',
    borderWidth: '0 0 1px 1px',
  },
});

const ConditionValue = ({
  value,
  property,
}: {
  value: string[] | undefined;
  property?: string;
}) => {
  const { sourceAccounts } = useSourceAccounts();

  if (property === 'apps') {
    return (
      <Stack
        direction='row'
        className='mt-[-4px]'
        sx={{
          gap: '4px',
        }}
      >
        {value?.map((v, i) => (
          <Tooltip title={v} key={i}>
            <CircleImage alt={v} src={ApplicationsLogosWithAliases[v]} />
          </Tooltip>
        ))}
      </Stack>
    );
  }

  if (property === 'environment') {
    return (
      <Typography
        variant='subtitle2'
        className='font-medium'
        sx={{ color: 'currentcolor' }}
      >
        {value?.map((x) => Environments[x]?.title).join(', ')}{' '}
      </Typography>
    );
  }

  if (property === 'resourceType') {
    return (
      <Typography
        variant='subtitle2'
        className='font-medium'
        sx={{ color: 'currentcolor' }}
      >
        {value
          ?.map((x) => ResourceTypes[x]?.displayName)
          .filter(Boolean)
          .join(', ')}
      </Typography>
    );
  }

  if (property === 'cloudProvider') {
    return (
      <Typography
        variant='subtitle2'
        className='font-medium'
        sx={{ color: 'currentcolor' }}
      >
        {value
          ?.map((x) => CloudProviders[x]?.displayName)
          .filter(Boolean)
          .join(', ')}
      </Typography>
    );
  }

  if (property === 'sensitivityLabels') {
    return (
      <Typography color='currentColor' variant='subtitle2Strong'>
        {value?.map((x) => SensitivityLevels[x]?.title ?? x).join(', ')}
      </Typography>
    );
  }

  if (property === 'accountId') {
    return (
      <Typography
        variant='subtitle2'
        className='font-medium'
        sx={{ color: 'currentcolor' }}
      >
        {value
          ?.map((x) => {
            const sourceAccount = sourceAccounts?.find(
              (a) => a.providerAccountId === x
            );
            const text = sourceAccount?.name || x;
            return text;
          })
          .filter(Boolean)
          .join(', ')}
      </Typography>
    );
  }

  return (
    <Typography
      variant='subtitle2'
      className='font-medium'
      sx={{ color: 'currentcolor' }}
    >
      {value?.join(', ')}
    </Typography>
  );
};

export function ConditionViewer({
  condition,
  compact,
  supportProperties,
  parent,
  children,
}: {
  condition: Condition;
  compact?: boolean;
  supportProperties: FilterProperty[];
  parent?: Condition;
  children?: React.ReactNode;
}) {
  const cls = classNames(condition.operator, { compact: compact });
  const property = supportProperties.find((c) => c.name === condition.property);

  switch (condition.operator) {
    case ConditionOperatorEnum.And:
    case ConditionOperatorEnum.Or:
      return (
        <ConditionGroup hideBorder={!parent} className={cls}>
          {condition.conditions?.map((c, i, arr) => (
            <ConditionViewer
              supportProperties={supportProperties}
              compact={compact}
              parent={condition}
              condition={c}
              key={i}
            >
              {i < arr.length - 1 && !compact && (
                <ConditionOperator>
                  {condition.operator.toUpperCase()}
                </ConditionOperator>
              )}
            </ConditionViewer>
          ))}
          {children}
        </ConditionGroup>
      );
    default:
      return (
        <ConditionContainer className={cls}>
          <Typography
            className='whitespace-nowrap'
            variant='subtitle2Strong'
            sx={{
              color: 'currentcolor',
            }}
          >
            {property?.displayName ?? condition.property ?? ''}
          </Typography>
          <Typography
            className='whitespace-nowrap'
            variant='subtitle2'
            sx={{ color: 'currentcolor' }}
          >
            &nbsp;
            {(property?.policyRelation &&
              OPERATORS[property.policyRelation][
                condition.operator as ConditionOperatorEnum
              ]?.label) ||
              condition.operator}
            &nbsp;
          </Typography>
          <ConditionValue
            property={condition.property}
            value={condition.value}
          />
          {children}
        </ConditionContainer>
      );
  }
}
